import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { PatientPicker } from '../components/PatientPicker';
import { PermissionGate } from '../components/PermissionGate';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  SectionHeader,
  Select,
  Skeleton,
  Textarea,
  type BadgeVariant,
  useActionDialog,
} from '../components/ui';
import { useAdmins } from '../features/admins/hooks/useAdmins';
import {
  useCompleteTask,
  useCreateTask,
  useDeleteTask,
  useTasks,
  useUpdateTask,
} from '../features/tasks/hooks/useTasks';
import type {
  Task,
  TaskPriority,
  TaskStatus,
} from '../types/admin';
import styles from './TasksPage.module.scss';

const STATUS_VARIANTS: Record<TaskStatus, BadgeVariant> = {
  pending: 'neutral',
  in_progress: 'info',
  completed: 'success',
  cancelled: 'danger',
};

const PRIORITY_VARIANTS: Record<TaskPriority, BadgeVariant> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
};

const STATUS_OPTIONS: readonly TaskStatus[] = [
  'pending',
  'in_progress',
  'completed',
  'cancelled',
];
const PRIORITY_OPTIONS: readonly TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

function toDateTimeLocalInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocalInputValue(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function formatDeadline(iso: string | null, locale: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

type AssigneeOption = {
  readonly admin_id: string;
  readonly label: string;
};

function assigneeLabel(assignee: { first_name: string | null; last_name: string | null; admin_id: string }): string {
  const name = [assignee.first_name, assignee.last_name].filter(Boolean).join(' ').trim();
  return name || assignee.admin_id;
}

export function TasksPage() {
  const { t, i18n } = useTranslation();
  const { confirmAction } = useActionDialog();

  const [statusFilter, setStatusFilter] = useState<TaskStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('');
  const [mineOnly, setMineOnly] = useState(false);
  const [search, setSearch] = useState('');

  const filters = useMemo(
    () => ({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(priorityFilter ? { priority: priorityFilter } : {}),
      ...(mineOnly ? { mine_only: true } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [statusFilter, priorityFilter, mineOnly, search],
  );

  const { data: tasks, isLoading, isError, error } = useTasks(filters);
  const adminsQuery = useAdmins({ page: 1, page_size: 100 });

  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const completeMutation = useCompleteTask();
  const deleteMutation = useDeleteTask();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Create form state
  const [cTitle, setCTitle] = useState('');
  const [cDescription, setCDescription] = useState('');
  const [cDeadline, setCDeadline] = useState('');
  const [cPriority, setCPriority] = useState<TaskPriority>('medium');
  const [cAssignees, setCAssignees] = useState<readonly string[]>([]);
  const [cRelatedPatientId, setCRelatedPatientId] = useState<string | null>(null);

  // Edit form state
  const [eTitle, setETitle] = useState('');
  const [eDescription, setEDescription] = useState('');
  const [eDeadline, setEDeadline] = useState('');
  const [ePriority, setEPriority] = useState<TaskPriority>('medium');
  const [eStatus, setEStatus] = useState<TaskStatus>('pending');
  const [eAssignees, setEAssignees] = useState<readonly string[]>([]);
  const [eRelatedPatientId, setERelatedPatientId] = useState<string | null>(null);

  const adminOptions: readonly AssigneeOption[] = useMemo(() => {
    const items = adminsQuery.data?.items ?? [];
    return items.map((admin) => ({
      admin_id: admin.admin_id,
      label: admin.full_name || admin.email,
    }));
  }, [adminsQuery.data]);

  function resetCreateForm() {
    setCTitle('');
    setCDescription('');
    setCDeadline('');
    setCPriority('medium');
    setCAssignees([]);
    setCRelatedPatientId(null);
  }

  function openCreate() {
    setEditingId(null);
    setShowCreate(true);
    resetCreateForm();
  }

  function openEdit(task: Task) {
    setShowCreate(false);
    setEditingId(task.id);
    setETitle(task.title);
    setEDescription(task.description ?? '');
    setEDeadline(toDateTimeLocalInputValue(task.deadline));
    setEPriority(task.priority);
    setEStatus(task.status);
    setEAssignees(task.assignees.map((a) => a.admin_id));
    setERelatedPatientId(task.related_patient_id ?? null);
  }

  function closeForms() {
    setShowCreate(false);
    setEditingId(null);
  }

  function toggleAssignee(
    list: readonly string[],
    setList: (next: readonly string[]) => void,
    adminId: string,
  ) {
    if (list.includes(adminId)) {
      setList(list.filter((id) => id !== adminId));
    } else {
      setList([...list, adminId]);
    }
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await createMutation.mutateAsync({
        title: cTitle.trim(),
        description: cDescription.trim() ? cDescription.trim() : null,
        deadline: fromDateTimeLocalInputValue(cDeadline),
        priority: cPriority,
        assignee_admin_ids: cAssignees,
        related_patient_id: cRelatedPatientId,
      });
      setShowCreate(false);
      resetCreateForm();
    } catch {
      return;
    }
  }

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({
        id: editingId,
        payload: {
          title: eTitle.trim(),
          description: eDescription.trim() ? eDescription.trim() : null,
          deadline: fromDateTimeLocalInputValue(eDeadline),
          priority: ePriority,
          status: eStatus,
          assignee_admin_ids: eAssignees,
          related_patient_id: eRelatedPatientId,
        },
      });
      setEditingId(null);
    } catch {
      return;
    }
  }

  function handleComplete(id: string) {
    completeMutation.mutate(id);
  }

  async function handleDelete(id: string) {
    if (!(await confirmAction({
      message: t('tasks.confirmDelete'),
      confirmLabel: t('dialog.delete'),
      tone: 'danger',
    }))) return;
    deleteMutation.mutate(id);
  }

  const busy =
    createMutation.isPending ||
    updateMutation.isPending ||
    completeMutation.isPending ||
    deleteMutation.isPending;

  if (isLoading) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('tasks.title')} />
          <div className={styles.skeletonList}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={88} />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  if (isError) {
    return (
      <AppShell>
        <div className={styles.page}>
          <SectionHeader title={t('tasks.title')} />
          <EmptyState
            title={t('tasks.loadErrorTitle')}
            description={error instanceof Error ? error.message : t('tasks.loadErrorDescription')}
          />
        </div>
      </AppShell>
    );
  }

  const list = tasks ?? [];
  const now = Date.now();

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader
          title={t('tasks.title')}
          actions={
            <PermissionGate permission="tasks:manage">
              <Button variant="primary" onClick={openCreate} disabled={busy}>
                {t('tasks.create')}
              </Button>
            </PermissionGate>
          }
        />

        <Card padding="md">
          <div className={styles.filters}>
            <Select
              label={t('tasks.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as TaskStatus | '')}
            >
              <option value="">{t('tasks.filterAny')}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {t(`tasks.statusLabels.${s}`)}
                </option>
              ))}
            </Select>
            <Select
              label={t('tasks.priority')}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | '')}
            >
              <option value="">{t('tasks.filterAny')}</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {t(`tasks.priorityLabels.${p}`)}
                </option>
              ))}
            </Select>
            <Input
              label={t('tasks.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('tasks.titlePlaceholder')}
            />
            <label className={styles.mineOnly}>
              <input
                type="checkbox"
                checked={mineOnly}
                onChange={(e) => setMineOnly(e.target.checked)}
              />
              {t('tasks.mineOnly')}
            </label>
          </div>
        </Card>

        {showCreate ? (
          <Card padding="none">
            <CardHeader title={t('tasks.newTaskTitle')} />
            <form onSubmit={handleCreate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('tasks.labelTitle')}
                    value={cTitle}
                    onChange={(e) => setCTitle(e.target.value)}
                    placeholder={t('tasks.titlePlaceholder')}
                  />
                  <Textarea
                    wrapperClassName={styles.fieldFull}
                    label={t('tasks.labelDescription')}
                    value={cDescription}
                    onChange={(e) => setCDescription(e.target.value)}
                    placeholder={t('tasks.descriptionPlaceholder')}
                    rows={4}
                  />
                  <Input
                    label={t('tasks.deadline')}
                    type="datetime-local"
                    value={cDeadline}
                    onChange={(e) => setCDeadline(e.target.value)}
                  />
                  <Select
                    label={t('tasks.priority')}
                    value={cPriority}
                    onChange={(e) => setCPriority(e.target.value as TaskPriority)}
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {t(`tasks.priorityLabels.${p}`)}
                      </option>
                    ))}
                  </Select>
                  <div className={styles.fieldFull}>
                    <label htmlFor="create-related-patient">{t('tasks.relatedPatient')}</label>
                    <PatientPicker
                      id="create-related-patient"
                      value={cRelatedPatientId}
                      onChange={setCRelatedPatientId}
                      placeholder={t('tasks.relatedPatientPlaceholder')}
                    />
                  </div>
                  <div className={styles.fieldFull}>
                    <div>{t('tasks.assignees')}</div>
                    <div className={styles.assigneeList}>
                      {adminOptions.length === 0 ? (
                        <span>{t('tasks.noAdmins')}</span>
                      ) : (
                        adminOptions.map((opt) => (
                          <label key={opt.admin_id} className={styles.assigneeRow}>
                            <input
                              type="checkbox"
                              checked={cAssignees.includes(opt.admin_id)}
                              onChange={() =>
                                toggleAssignee(cAssignees, setCAssignees, opt.admin_id)
                              }
                            />
                            {opt.label}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {createMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{createMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('tasks.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={busy || !cTitle.trim() || cAssignees.length === 0}
                >
                  {createMutation.isPending ? t('tasks.creating') : t('tasks.create')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {editingId ? (
          <Card padding="none">
            <CardHeader title={t('tasks.editTaskTitle')} />
            <form onSubmit={handleUpdate}>
              <CardBody>
                <div className={styles.formGrid}>
                  <Input
                    wrapperClassName={styles.fieldFull}
                    label={t('tasks.labelTitle')}
                    value={eTitle}
                    onChange={(e) => setETitle(e.target.value)}
                  />
                  <Textarea
                    wrapperClassName={styles.fieldFull}
                    label={t('tasks.labelDescription')}
                    value={eDescription}
                    onChange={(e) => setEDescription(e.target.value)}
                    rows={4}
                  />
                  <Input
                    label={t('tasks.deadline')}
                    type="datetime-local"
                    value={eDeadline}
                    onChange={(e) => setEDeadline(e.target.value)}
                  />
                  <Select
                    label={t('tasks.priority')}
                    value={ePriority}
                    onChange={(e) => setEPriority(e.target.value as TaskPriority)}
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <option key={p} value={p}>
                        {t(`tasks.priorityLabels.${p}`)}
                      </option>
                    ))}
                  </Select>
                  <Select
                    label={t('tasks.status')}
                    value={eStatus}
                    onChange={(e) => setEStatus(e.target.value as TaskStatus)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {t(`tasks.statusLabels.${s}`)}
                      </option>
                    ))}
                  </Select>
                  <div className={styles.fieldFull}>
                    <label htmlFor="edit-related-patient">{t('tasks.relatedPatient')}</label>
                    <PatientPicker
                      id="edit-related-patient"
                      value={eRelatedPatientId}
                      onChange={setERelatedPatientId}
                      placeholder={t('tasks.relatedPatientPlaceholder')}
                    />
                  </div>
                  <div className={styles.fieldFull}>
                    <div>{t('tasks.assignees')}</div>
                    <div className={styles.assigneeList}>
                      {adminOptions.length === 0 ? (
                        <span>{t('tasks.noAdmins')}</span>
                      ) : (
                        adminOptions.map((opt) => (
                          <label key={opt.admin_id} className={styles.assigneeRow}>
                            <input
                              type="checkbox"
                              checked={eAssignees.includes(opt.admin_id)}
                              onChange={() =>
                                toggleAssignee(eAssignees, setEAssignees, opt.admin_id)
                              }
                            />
                            {opt.label}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                {updateMutation.error instanceof Error ? (
                  <p className={styles.inlineError}>{updateMutation.error.message}</p>
                ) : null}
              </CardBody>
              <CardFooter>
                <Button variant="secondary" type="button" onClick={closeForms} disabled={busy}>
                  {t('tasks.cancel')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={busy || !eTitle.trim() || eAssignees.length === 0}
                >
                  {updateMutation.isPending ? t('tasks.saving') : t('tasks.save')}
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : null}

        {list.length === 0 && !showCreate ? (
          <EmptyState title={t('tasks.noTasks')} description={t('tasks.noTasksDescription')} />
        ) : list.length > 0 ? (
          <div className={styles.list}>
            {list.map((task) => {
              const overdue =
                task.deadline != null &&
                task.status !== 'completed' &&
                task.status !== 'cancelled' &&
                new Date(task.deadline).getTime() < now;
              return (
                <Card key={task.id} padding="md">
                  <div className={styles.row}>
                    <div className={styles.identity}>
                      <div className={styles.titleLine}>
                        <span className={styles.taskTitle}>{task.title}</span>
                        <Badge variant={STATUS_VARIANTS[task.status]} size="sm">
                          {t(`tasks.statusLabels.${task.status}`)}
                        </Badge>
                        <Badge variant={PRIORITY_VARIANTS[task.priority]} size="sm">
                          {t(`tasks.priorityLabels.${task.priority}`)}
                        </Badge>
                        <Badge variant="neutral" size="sm">
                          {t(`tasks.sourceLabels.${task.source}`)}
                        </Badge>
                      </div>
                      {task.description ? (
                        <p className={styles.description}>{task.description}</p>
                      ) : null}
                      <div className={styles.meta}>
                        {task.deadline ? (
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>{t('tasks.deadline')}</span>
                            <span
                              className={`${styles.deadline} ${overdue ? styles.deadlineOverdue : ''}`}
                            >
                              {formatDeadline(task.deadline, i18n.language)}
                              {overdue ? ` (${t('tasks.overdue')})` : ''}
                            </span>
                          </span>
                        ) : null}
                        {task.related_patient_id ? (
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>{t('tasks.relatedPatient')}</span>
                            {task.related_patient_name || task.related_patient_id}
                          </span>
                        ) : null}
                        {task.related_payment_id ? (
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>{t('tasks.relatedPayment')}</span>
                            {task.related_payment_id}
                          </span>
                        ) : null}
                      </div>
                      {task.assignees.length > 0 ? (
                        <div className={styles.meta}>
                          <span className={styles.metaItem}>
                            <span className={styles.metaLabel}>{t('tasks.assignees')}</span>
                            <span className={styles.chips}>
                              {task.assignees.map((a) => (
                                <Badge key={a.admin_id} variant="accent" size="sm">
                                  {assigneeLabel(a)}
                                </Badge>
                              ))}
                            </span>
                          </span>
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.rowActions}>
                      {task.status !== 'completed' && task.status !== 'cancelled' ? (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleComplete(task.id)}
                          disabled={busy}
                        >
                          {t('tasks.complete')}
                        </Button>
                      ) : null}
                      <PermissionGate permission="tasks:manage">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(task)}
                          disabled={busy || showCreate || (editingId !== null && editingId !== task.id)}
                        >
                          {t('tasks.edit')}
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(task.id)}
                          disabled={busy}
                        >
                          {t('tasks.delete')}
                        </Button>
                      </PermissionGate>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
